import { ApiCreditcardWithSecurityCode } from '@/models/api/creditcard'
import { CreditcardToBuildPaymentPayload } from '@/services/api/creditcard'
import { Either } from 'fp-ts/lib/Either'

import { CreditcardPayment } from '@/services/payment/types'
import { ScriptLoader } from '@/util/scriptLoader'
import { User } from '@spa/store/modules/user/types'

export interface CreditcardPaymentProviderViewConfig {
  readonly registerCardForm: {
    readonly requireCardOwner: boolean
    readonly requireSecurityCode: boolean
  }
  readonly paymentForm: {
    readonly requireSecurityCode: boolean
  }
}

export type CreditcardPayment = Required<Pick<
  ApiCreditcardWithSecurityCode,
  'id' | 'securityCode' | 'lastCreditDate' | 'numberLast4' | 'cardType'
>>

/**
 * クレジットカード決済を提供するプロバイダとのやり取りを行うモジュール
 */
export abstract class CreditcardPaymentProviderService {
  readonly abstract registerCardLimit: number | null

  protected readonly abstract scriptSrc: string
  protected readonly abstract namespace: string

  async loadScript() {
    const scriptLoader = ScriptLoader.getInstanceFor({
      src: this.scriptSrc,
      global: this.namespace,
    })

    await scriptLoader.load()

    if (!this.isScriptLoaded) {
      Promise.reject(new Error('loaded script is invalid.'))
    }
  }

  abstract get isScriptLoaded(): boolean

  abstract async createRegisterCardPayload(
    creditcard: {
      cardNumber: string
      expireYear: string
      expireMonth: string
      securityCode: string
      cardOwner: string
    },
    user: User
  ): Promise<Either<Error, string>>
}

export interface CreditcardPaymentProviderServiceForPurchase extends CreditcardPaymentProviderService {
  readonly _context: 'for-purchase'

  /**
   * 単体購入ではクレジッドカード決済が3D決済をサポートしている場合もあるので、
   * payload生成時に利用する決済種別も要求する.
   * @see https://github.com/Everforth/growthpalette-front/issues/7868
   */
  createPaymentPayload(
    payment: Pick<CreditcardPayment, 'payment_type'>,
    creditcard: CreditcardToBuildPaymentPayload,
    user: User
  ): Promise<Either<Error, CreditcardPayment>>
}
export interface CreditcardPaymentProviderServiceForSubscription extends CreditcardPaymentProviderService {
  readonly _context: 'for-subscription'

  /**
   * 定期決済系（サブスク、有料会員など）では、2024/06/06時点では3Dセキュアはサポートせず通常のカード決済で一定.
   * よって、payload生成時には決済種別を要求しない.
   * @see https://github.com/Everforth/growthpalette-front/issues/7868
   * @see https://github.com/my-color/front/blob/develop/src/Controller/Api/User/Payment/Subscription/FilterOnlyAvailableOnSubscription.php
   * @note
   * 定期決済系でも3Dセキュアをサポートし、かつ実行時の状態に応じて3Dセキュアかどうかを使い分ける必要がある場合には、
   * {@link CreditcardPaymentProviderServiceForPurchase}とI/Fを揃えても良い
   * 下記の理由から、ここではI/Fを分割する方針を取った
   * - 上記Issueの対応時点では定期決済系はスコープ外であるため、対応の範囲を絞りたい
   * - I/Fを分けることで、「定期決済系はpaymentに影響されない」という前提を明示する
   * - 定期決済系でも3Dセキュアをサポートするとして、後からI/Fを統合する場合も影響範囲は定期決済系に閉じる
   * - そもそも定期決済系で3Dセキュアをサポートしない間は、3Dセキュアの変更も単発購入の範囲内に限定される
   */
  createPaymentPayload(
    creditcard: CreditcardToBuildPaymentPayload,
    user: User
  ): Promise<Either<Error, CreditcardPayment>>
}
